import { Box, Paper, Typography, alpha, Grid, IconButton, Button } from '@mui/material';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import CurrencyExchangeIcon from '@mui/icons-material/CurrencyExchange';
import CandlestickChartIcon from '@mui/icons-material/CandlestickChart';
import DiamondIcon from '@mui/icons-material/Diamond';
import PersonIcon from '@mui/icons-material/Person';

import { styled } from '@mui/material/styles';
import { useState } from 'react';
import VisibilityIcon from '@mui/icons-material/Visibility';
import useAuth from '../../../hooks/useAuth';
import TopGainersLosersModal from './components/TopGainersLosersModal';
import ActiveTradesDetailsModal from './components/ActiveTradesDetailsModal';

// Right side panel layout (55% width) with consistent card sizing
const HorizontalCardsPanel = styled(Box)(({ theme }) => ({
    width: '100%',
    minHeight: 'calc(100vh - 200px)',
    backgroundColor: 'transparent',
    padding: theme.spacing(0.25),
    borderRadius: 0,
    border: 'none',
    boxShadow: 'none',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.25), // Reduced from 0.5 to 0.25
    overflow: 'auto',
    '&::-webkit-scrollbar': {
        width: '6px',
    },
    '&::-webkit-scrollbar-track': {
        backgroundColor: alpha(theme.palette.grey[300], 0.2),
        borderRadius: '3px',
    },
    '&::-webkit-scrollbar-thumb': {
        backgroundColor: alpha(theme.palette.grey[500], 0.3),
        borderRadius: '3px',
        '&:hover': {
            backgroundColor: alpha(theme.palette.grey[500], 0.5),
        }
    },
    [theme.breakpoints.down('md')]: {
        minHeight: 'auto',
        gap: theme.spacing(0.2), // Reduced from 0.5 to 0.2
        padding: theme.spacing(0.1),
    },
    [theme.breakpoints.down('sm')]: {
        gap: theme.spacing(0.3), // Reduced from 0.8 to 0.3
        padding: theme.spacing(0.25),
    }
}));

// System Overview Section with better spacing
const SystemOverviewSection = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.25), // Reduced from 0.5 to 0.25
    marginBottom: theme.spacing(0.25), // Reduced from 0.5 to 0.25
    '& .section-title': {
        fontSize: '1rem',
        fontWeight: 600,
        color: theme.palette.text.primary,
        marginBottom: theme.spacing(0.1),
        // padding: theme.spacing(0.25, 0),
        borderBottom: `2px solid ${theme.palette.primary.main}`,
        display: 'inline-block',
    }
}));

// Cards Grid Layout with consistent spacing
const CardsGrid = styled(Grid)(({ theme }) => ({
    margin: 0,
    width: '100%',
    '& .MuiGrid-item': {
        display: 'flex',
        paddingLeft: theme.spacing(0.5),
    },
    '& .MuiGrid-container': {
        margin: 0,
        width: '100%',
    }
}));

const cardColors = [
    {
        background: 'linear-gradient(135deg, #FF9066 0%, #FF5722 100%)', // Orange gradient
        textColor: '#FFFFFF',
        iconColor: '#FFE0B2'
    },
    {
        background: 'linear-gradient(135deg, #42A5F5 0%, #1E88E5 100%)', // Blue gradient  
        textColor: '#FFFFFF',
        iconColor: '#BBDEFB'
    },
    {
        background: 'linear-gradient(135deg, #AB47BC 0%, #8E24AA 100%)', // Purple gradient
        textColor: '#FFFFFF',
        iconColor: '#E1BEE7'
    }
];

// Stat card styles - Dynamic height for right panel
const StatsCard = styled(Paper, {
    shouldForwardProp: (prop) => prop !== 'colorIndex'
})(({ theme, colorIndex }) => ({
    padding: theme.spacing(1),
    minHeight: 'auto',
    width: '100%',
    background: cardColors[colorIndex]?.background || cardColors[0].background,
    color: cardColors[colorIndex]?.textColor || '#FFFFFF',
    border: 'none',
    borderRadius: '12px',
    transition: 'all 0.3s ease-in-out',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)',
    position: 'relative',
    overflow: 'hidden',
    '&:before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(255, 255, 255, 0.1)',
        borderRadius: '12px',
        border: '1px solid rgba(255, 255, 255, 0.2)',
        pointerEvents: 'none',
    },
    '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: '0 6px 20px rgba(0, 0, 0, 0.15)',
    },
    [theme.breakpoints.down('lg')]: {
        padding: theme.spacing(0.9),
    },
    [theme.breakpoints.down('md')]: {
        padding: theme.spacing(0.8),
    },
    [theme.breakpoints.down('sm')]: {
        padding: theme.spacing(0.7),
    }
}));

const StatItem = styled(Box)(({ theme, isGrossPL }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(0.5, 0),
    borderBottom: `1px solid rgba(255, 255, 255, 0.1)`,
    '& .MuiTypography-body2': {
        fontSize: '0.75rem',
        fontWeight: 500,
        color: 'rgba(255, 255, 255, 0.9)',
        letterSpacing: '0.01em',
    },
    '& .MuiTypography-subtitle1': {
        fontSize: '0.85rem',
        fontWeight: 700,
        color: '#FFFFFF',
        letterSpacing: '0.02em',
    },
    ...(isGrossPL && {
        marginTop: theme.spacing(0.25),
        backgroundColor: 'rgba(255, 255, 255, 0.15)',
        borderRadius: '8px',
        padding: theme.spacing(0.5, 0.75),
        border: '1px solid rgba(255, 255, 255, 0.2)',
        '& .MuiTypography-subtitle1': {
            color: '#FFFFFF',
            fontWeight: 800,
            fontSize: '0.9rem'
        },
        '& .MuiTypography-body2': {
            fontWeight: 600,
            fontSize: '0.75rem',
            color: 'rgba(255, 255, 255, 0.95)',
        }
    }), '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.05)',
        borderRadius: '6px',
    },
    '&:last-child': {
        borderBottom: 'none',
        paddingBottom: 0
    },
    '&:first-of-type': {
        paddingTop: 0
    }
}));

const TableHeader = styled(Box)(({ theme }) => ({
    display: 'grid',
    gridTemplateColumns: '1.2fr 1fr 1fr 1fr',
    gap: theme.spacing(0.5),
    padding: theme.spacing(0.75),
    backgroundColor: 'rgba(255, 255, 255, 0.15)',
    borderRadius: '8px',
    marginBottom: theme.spacing(0.2), // Reduced from 0.5 to 0.2
    border: '1px solid rgba(255, 255, 255, 0.2)',
    '& .header-cell': {
        color: '#FFFFFF',
        fontSize: '0.75rem',
        fontWeight: 700,
        textTransform: 'uppercase',
        textAlign: 'center',
        letterSpacing: '0.03em',
        '&:first-of-type': {
            textAlign: 'left',
        }
    }
}));

const TableRow = styled(Box)(({ theme }) => ({
    display: 'grid',
    gridTemplateColumns: '1.2fr 1fr 1fr 1fr',
    gap: theme.spacing(0.5),
    padding: theme.spacing(0.5, 0.75),
    borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
    alignItems: 'center',
    '&:last-child': {
        borderBottom: 'none',
    },
    '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.05)',
        borderRadius: '6px',
    },
    '& .symbol': {
        fontSize: '0.75rem',
        fontWeight: 600,
        color: '#FFFFFF',
        letterSpacing: '0.02em',
    },
    '& .value': {
        fontSize: '0.75rem',
        fontWeight: 600,
        textAlign: 'center',
        padding: theme.spacing(0.4, 0.6),
        borderRadius: '6px',
        backgroundColor: 'rgba(255, 255, 255, 0.1)',
        letterSpacing: '0.02em',
        color: '#FFFFFF',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        '&.positive': {
            color: '#4CAF50',
            backgroundColor: 'rgba(76, 175, 80, 0.15)',
            border: '1px solid rgba(76, 175, 80, 0.3)',
        },
        '&.negative': {
            color: '#F44336',
            backgroundColor: 'rgba(244, 67, 54, 0.15)',
            border: '1px solid rgba(244, 67, 54, 0.3)',
        },
    },
    '& .total': {
        fontWeight: 700,
        color: '#FFFFFF',
        backgroundColor: 'rgba(255, 255, 255, 0.15)',
        border: '1px solid rgba(255, 255, 255, 0.3)',
    }
}));

const CompactCard = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(0.75),
    minHeight: 'auto',
    width: '100%',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: '#FFFFFF',
    border: 'none',
    borderRadius: '12px',
    transition: 'all 0.3s ease-in-out',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 4px 16px rgba(102, 126, 234, 0.2)',
    position: 'relative',
    overflow: 'hidden',
    marginBottom: theme.spacing(0.1), // Reduced from 0.5 to 0.1
    '&:before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(255, 255, 255, 0.1)',
        borderRadius: '12px',
        border: '1px solid rgba(255, 255, 255, 0.2)',
        pointerEvents: 'none',
    },
    '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: '0 6px 20px rgba(102, 126, 234, 0.3)',
    },
    [theme.breakpoints.down('sm')]: {
        padding: theme.spacing(0.6),
    }
}));

const CardHeader = styled(Box)(({ theme }) => ({
    margin: theme.spacing(-2.5, -2.5, 2),
    padding: theme.spacing(2.5, 2.5, 2),
    transition: 'background-color 0.3s ease',
    '& .MuiTypography-h6': {
        fontSize: '1.1rem',
        fontWeight: 600,
        color: theme.palette.text.primary,
        letterSpacing: '0.015em'
    }
}));

// White background card for NSE and MCX Stats - Dynamic sizing
const WhiteStatsCard = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(0.75),
    minHeight: 'auto',
    width: '100%',
    background: '#FFFFFF',
    color: theme.palette.text.primary,
    border: '1px solid #E0E0E0',
    borderRadius: '12px',
    transition: 'all 0.3s ease-in-out',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
    position: 'relative',
    overflow: 'hidden',
    marginBottom: theme.spacing(0.1), // Reduced from 0.5 to 0.1
    '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
    },
    [theme.breakpoints.down('sm')]: {
        padding: theme.spacing(0.6),
    }
}));

// Dynamic PL Overview card - Dynamic sizing
const PLOverviewCard = styled(Paper, {
    shouldForwardProp: (prop) => prop !== 'isPositive'
})(({ theme, isPositive }) => ({
    padding: theme.spacing(0.75),
    minHeight: 'auto',
    width: '100%',
    background: isPositive
        ? 'linear-gradient(135deg, #E8F5E8 0%, #F1F8E9 100%)'
        : 'linear-gradient(135deg, #FFEBEE 0%, #FFCDD2 100%)',
    color: theme.palette.text.primary,
    border: isPositive
        ? '1px solid #C8E6C9'
        : '1px solid #FFCDD2',
    borderRadius: '12px',
    transition: 'all 0.3s ease-in-out',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: isPositive
        ? '0 2px 8px rgba(76, 175, 80, 0.1)'
        : '0 2px 8px rgba(244, 67, 54, 0.1)', position: 'relative',
    overflow: 'hidden',
    marginBottom: theme.spacing(0.1), // Reduced from 0.5 to 0.1
    '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: isPositive
            ? '0 4px 12px rgba(76, 175, 80, 0.15)'
            : '0 4px 12px rgba(244, 67, 54, 0.15)',
    },
    [theme.breakpoints.down('sm')]: {
        padding: theme.spacing(0.6),
    }
}));

// White table header for stats cards
const WhiteTableHeader = styled(Box)(({ theme }) => ({
    display: 'grid',
    gridTemplateColumns: '1.2fr 1fr 1fr 1fr',
    gap: theme.spacing(0.4),
    padding: theme.spacing(0.5), backgroundColor: '#F5F5F5',
    borderRadius: '8px',
    marginBottom: theme.spacing(0.2), // Reduced from 0.4 to 0.2
    border: '1px solid #E0E0E0',
    '& .header-cell': {
        color: theme.palette.text.primary,
        fontSize: '0.7rem',
        fontWeight: 700,
        textTransform: 'uppercase',
        textAlign: 'center',
        letterSpacing: '0.03em',
        '&:first-of-type': {
            textAlign: 'left',
        }
    }
}));

// White table row for stats cards
const WhiteTableRow = styled(Box)(({ theme }) => ({
    display: 'grid',
    gridTemplateColumns: '1.2fr 1fr 1fr 1fr',
    gap: theme.spacing(0.4),
    padding: theme.spacing(0.4, 0.5),
    borderBottom: '1px solid #F0F0F0',
    alignItems: 'center',
    '&:last-child': {
        borderBottom: 'none',
    },
    '&:hover': {
        backgroundColor: '#F8F8F8',
        borderRadius: '6px',
    },
    '& .symbol': {
        fontSize: '0.7rem',
        fontWeight: 600,
        color: theme.palette.text.primary,
        letterSpacing: '0.02em',
    },
    '& .value': {
        fontSize: '0.7rem',
        fontWeight: 600,
        textAlign: 'center',
        padding: theme.spacing(0.3, 0.5),
        borderRadius: '6px',
        backgroundColor: '#F5F5F5',
        letterSpacing: '0.02em',
        color: theme.palette.text.primary,
        border: '1px solid #E0E0E0',
        '&.positive': {
            color: '#2E7D32',
            backgroundColor: 'rgba(76, 175, 80, 0.1)',
            border: '1px solid rgba(76, 175, 80, 0.3)',
        },
        '&.negative': {
            color: '#C62828',
            backgroundColor: 'rgba(244, 67, 54, 0.1)',
            border: '1px solid rgba(244, 67, 54, 0.3)',
        },
    },
    '& .total': {
        fontWeight: 700,
        color: theme.palette.text.primary,
        backgroundColor: '#E3F2FD',
        border: '1px solid #90CAF9',
    }
}));

// Helper function to safely get numeric values
function safeNumeric(value) {
    if (value === undefined || value === null || value === '') {
        return 0;
    }
    const num = Number(value);
    return Number.isNaN(num) ? 0 : num;
}

// Method to round and format a number in Indian number system format
function formatValue(value) {
    // Handle undefined, null, or NaN values
    if (value === undefined || value === null || Number.isNaN(value)) {
        return '0';
    }
    const roundedValue = Math.round(value);
    // Format using Indian number system (e.g., 11,00,000 instead of 1,100,000)
    return roundedValue.toLocaleString('en-IN') === '-0' ? '0' : roundedValue.toLocaleString('en-IN');
}

const getValueColor = (value) => {
    if (typeof value === 'string') {
        return value.includes('-') ? '#c62828' : '#1b5e20'; // Dark red and dark green
    }
    return value < 0 ? '#c62828' : '#1b5e20'; // Dark red and dark green
};

const DashboardCardsPanel = ({ balanceDetail = {}, userCounts = {}, onUsernameClick, selectedBroker, selectedAdmin, isBrokerView, isAdminView }) => {
    // Import useAuth to get user role information
    const { user } = useAuth();

    // State for Top Gainers/Losers Modal
    const [modalOpen, setModalOpen] = useState(false);
    const [modalSegment, setModalSegment] = useState('NSE');

    // State for Active Trades Details Modal
    const [activeTradesModalOpen, setActiveTradesModalOpen] = useState(false);
    const [activeTradesSegment, setActiveTradesSegment] = useState('NSE');

    // Get current user ID for API call
    const currentUserId = selectedBroker?.id || selectedAdmin?.id || user?._id;

    // Handler to open top gainers/losers modal
    const handleOpenModal = (segment) => {
        setModalSegment(segment);
        setModalOpen(true);
    };

    const handleCloseModal = () => {
        setModalOpen(false);
    };

    // Handler to open active trades details modal
    const handleOpenActiveTradesModal = (segment) => {
        setActiveTradesSegment(segment);
        setActiveTradesModalOpen(true);
    };

    const handleCloseActiveTradesModal = () => {
        setActiveTradesModalOpen(false);
    };

    // Helper to safely aggregate values across all segments
    const safeAdd = (...values) => {
        return values.reduce((acc, val) => acc + (val || 0), 0);
    };

    // Aggregate financial data across all segments (NSE, MCX, COMEX, WINDEX, NSEIX)
    const aggregatedData = {
        activePl: {
            your: safeAdd(
                balanceDetail?.NSE?.activeProfitLoss?.broker,
                balanceDetail?.MCX?.activeProfitLoss?.broker,
                balanceDetail?.COMEX?.activeProfitLoss?.broker,
                balanceDetail?.WINDEX?.activeProfitLoss?.broker,
                balanceDetail?.NSEIX?.activeProfitLoss?.broker
            ),
            total: safeAdd(
                balanceDetail?.NSE?.activeProfitLoss?.total,
                balanceDetail?.MCX?.activeProfitLoss?.total,
                balanceDetail?.COMEX?.activeProfitLoss?.total,
                balanceDetail?.WINDEX?.activeProfitLoss?.total,
                balanceDetail?.NSEIX?.activeProfitLoss?.total
            ),
            admin: safeAdd(
                balanceDetail?.NSE?.activeProfitLoss?.admin,
                balanceDetail?.MCX?.activeProfitLoss?.admin,
                balanceDetail?.COMEX?.activeProfitLoss?.admin,
                balanceDetail?.WINDEX?.activeProfitLoss?.admin,
                balanceDetail?.NSEIX?.activeProfitLoss?.admin
            ),
            superAdmin: safeAdd(
                balanceDetail?.NSE?.activeProfitLoss?.superAdmin,
                balanceDetail?.MCX?.activeProfitLoss?.superAdmin,
                balanceDetail?.COMEX?.activeProfitLoss?.superAdmin,
                balanceDetail?.WINDEX?.activeProfitLoss?.superAdmin,
                balanceDetail?.NSEIX?.activeProfitLoss?.superAdmin
            )
        },
        profitLoss: {
            your: safeAdd(
                balanceDetail?.NSE?.profitLoss?.broker,
                balanceDetail?.MCX?.profitLoss?.broker,
                balanceDetail?.COMEX?.profitLoss?.broker,
                balanceDetail?.WINDEX?.profitLoss?.broker,
                balanceDetail?.NSEIX?.profitLoss?.broker
            ),
            total: safeAdd(
                balanceDetail?.NSE?.profitLoss?.total,
                balanceDetail?.MCX?.profitLoss?.total,
                balanceDetail?.COMEX?.profitLoss?.total,
                balanceDetail?.WINDEX?.profitLoss?.total,
                balanceDetail?.NSEIX?.profitLoss?.total
            ),
            admin: safeAdd(
                balanceDetail?.NSE?.profitLoss?.admin,
                balanceDetail?.MCX?.profitLoss?.admin,
                balanceDetail?.COMEX?.profitLoss?.admin,
                balanceDetail?.WINDEX?.profitLoss?.admin,
                balanceDetail?.NSEIX?.profitLoss?.admin
            ),
            superAdmin: safeAdd(
                balanceDetail?.NSE?.profitLoss?.superAdmin,
                balanceDetail?.MCX?.profitLoss?.superAdmin,
                balanceDetail?.COMEX?.profitLoss?.superAdmin,
                balanceDetail?.WINDEX?.profitLoss?.superAdmin,
                balanceDetail?.NSEIX?.profitLoss?.superAdmin
            )
        },
        brokerage: {
            your: safeAdd(
                balanceDetail?.NSE?.brokerage?.broker,
                balanceDetail?.MCX?.brokerage?.broker,
                balanceDetail?.COMEX?.brokerage?.broker,
                balanceDetail?.WINDEX?.brokerage?.broker,
                balanceDetail?.NSEIX?.brokerage?.broker
            ),
            total: safeAdd(
                balanceDetail?.NSE?.brokerage?.total,
                balanceDetail?.MCX?.brokerage?.total,
                balanceDetail?.COMEX?.brokerage?.total,
                balanceDetail?.WINDEX?.brokerage?.total,
                balanceDetail?.NSEIX?.brokerage?.total
            ),
            admin: safeAdd(
                balanceDetail?.NSE?.brokerage?.admin,
                balanceDetail?.MCX?.brokerage?.admin,
                balanceDetail?.COMEX?.brokerage?.admin,
                balanceDetail?.WINDEX?.brokerage?.admin,
                balanceDetail?.NSEIX?.brokerage?.admin
            ),
            superAdmin: safeAdd(
                balanceDetail?.NSE?.brokerage?.superAdmin,
                balanceDetail?.MCX?.brokerage?.superAdmin,
                balanceDetail?.COMEX?.brokerage?.superAdmin,
                balanceDetail?.WINDEX?.brokerage?.superAdmin,
                balanceDetail?.NSEIX?.brokerage?.superAdmin
            )
        },
        grossProfitLoss: {
            your: safeAdd(
                balanceDetail?.NSE?.grossProfitLoss?.broker,
                balanceDetail?.MCX?.grossProfitLoss?.broker,
                balanceDetail?.COMEX?.grossProfitLoss?.broker,
                balanceDetail?.WINDEX?.grossProfitLoss?.broker,
                balanceDetail?.NSEIX?.grossProfitLoss?.broker
            ),
            total: safeAdd(
                balanceDetail?.NSE?.grossProfitLoss?.total,
                balanceDetail?.MCX?.grossProfitLoss?.total,
                balanceDetail?.COMEX?.grossProfitLoss?.total,
                balanceDetail?.WINDEX?.grossProfitLoss?.total,
                balanceDetail?.NSEIX?.grossProfitLoss?.total
            ),
            admin: safeAdd(
                balanceDetail?.NSE?.grossProfitLoss?.admin,
                balanceDetail?.MCX?.grossProfitLoss?.admin,
                balanceDetail?.COMEX?.grossProfitLoss?.admin,
                balanceDetail?.WINDEX?.grossProfitLoss?.admin,
                balanceDetail?.NSEIX?.grossProfitLoss?.admin
            ),
            superAdmin: safeAdd(
                balanceDetail?.NSE?.grossProfitLoss?.superAdmin,
                balanceDetail?.MCX?.grossProfitLoss?.superAdmin,
                balanceDetail?.COMEX?.grossProfitLoss?.superAdmin,
                balanceDetail?.WINDEX?.grossProfitLoss?.superAdmin,
                balanceDetail?.NSEIX?.grossProfitLoss?.superAdmin
            )
        }
    };

    // Get segment-level active buy/sell data for market data display
    const marketData = {
        comex: [
            {
                name: 'COMEX Total',
                buy: formatValue(safeNumeric(balanceDetail?.COMEX?.activeBuy)),
                sell: formatValue(safeNumeric(balanceDetail?.COMEX?.activeSell)),
                total: formatValue(safeNumeric(balanceDetail?.COMEX?.activeBuy) - safeNumeric(balanceDetail?.COMEX?.activeSell))
            }
        ],
        windex: [
            {
                name: 'WINDEX Total',
                buy: formatValue(safeNumeric(balanceDetail?.WINDEX?.activeBuy)),
                sell: formatValue(safeNumeric(balanceDetail?.WINDEX?.activeSell)),
                total: formatValue(safeNumeric(balanceDetail?.WINDEX?.activeBuy) - safeNumeric(balanceDetail?.WINDEX?.activeSell))
            }
        ],
        crypto: [
            {
                name: 'CRYPTO Total',
                buy: formatValue(safeNumeric(balanceDetail?.CRYPTO?.activeBuy)),
                sell: formatValue(safeNumeric(balanceDetail?.CRYPTO?.activeSell)),
                total: formatValue(safeNumeric(balanceDetail?.CRYPTO?.activeBuy) - safeNumeric(balanceDetail?.CRYPTO?.activeSell))
            }
        ]
    };    // Determine cards based on view and role
    let cards = [];
    let cardGridCols = 4; // Default grid columns for 3 cards

    if (isBrokerView) {
        cards = [
            {
                title: 'Broker',
                stats: {
                    ActivePL: formatValue(aggregatedData.activePl.your),
                    BookedPL: formatValue(aggregatedData.profitLoss.your),
                    Brokerage: formatValue(aggregatedData.brokerage.your),
                    GrossPL: formatValue(aggregatedData.grossProfitLoss.your)
                }
            },
            {
                title: 'Clients',
                stats: {
                    ActivePL: formatValue(aggregatedData.activePl.total),
                    BookedPL: formatValue(aggregatedData.profitLoss.total),
                    Brokerage: formatValue(aggregatedData.brokerage.total),
                    GrossPL: formatValue(aggregatedData.grossProfitLoss.total)
                }
            },
            {
                title: 'Admin',  // In broker view, the third card is Admin instead of SuperAdmin
                stats: {
                    ActivePL: formatValue(aggregatedData.activePl.admin),
                    BookedPL: formatValue(aggregatedData.profitLoss.admin),
                    Brokerage: formatValue(aggregatedData.brokerage.admin),
                    GrossPL: formatValue(aggregatedData.grossProfitLoss.admin)
                }
            }
        ];
        cardGridCols = 4; // 3 cards
    } else if (isAdminView) {
        cards = [
            {
                title: 'Admin',
                stats: {
                    ActivePL: formatValue(aggregatedData.activePl.your),
                    BookedPL: formatValue(aggregatedData.profitLoss.your),
                    Brokerage: formatValue(aggregatedData.brokerage.your),
                    GrossPL: formatValue(aggregatedData.grossProfitLoss.your)
                }
            },
            {
                title: 'Clients',
                stats: {
                    ActivePL: formatValue(aggregatedData.activePl.total),
                    BookedPL: formatValue(aggregatedData.profitLoss.total),
                    Brokerage: formatValue(aggregatedData.brokerage.total),
                    GrossPL: formatValue(aggregatedData.grossProfitLoss.total)
                }
            },
            {
                title: 'SuperAdmin',
                stats: {
                    ActivePL: formatValue(aggregatedData.activePl.admin),
                    BookedPL: formatValue(aggregatedData.profitLoss.admin),
                    Brokerage: formatValue(aggregatedData.brokerage.admin),
                    GrossPL: formatValue(aggregatedData.grossProfitLoss.admin)
                }
            }
        ];
        cardGridCols = 4; // 3 cards
    } else if (user?.role?.rolename === 'superAdmin') {
        cards = [
            {
                title: 'SuperAdmin',  // Show SuperAdmin for superAdmin role
                stats: {
                    ActivePL: formatValue(aggregatedData.activePl.your),
                    BookedPL: formatValue(aggregatedData.profitLoss.your),
                    Brokerage: formatValue(aggregatedData.brokerage.your),
                    GrossPL: formatValue(aggregatedData.grossProfitLoss.your)
                }
            },
            {
                title: 'Client',  // Show Admin for superAdmin role (second card)
                stats: {
                    ActivePL: formatValue(aggregatedData.activePl.total),
                    BookedPL: formatValue(aggregatedData.profitLoss.total),
                    Brokerage: formatValue(aggregatedData.brokerage.total),
                    GrossPL: formatValue(aggregatedData.grossProfitLoss.total)
                }
            },
            {
                title: 'Admin',  // Show Admin for superAdmin role (second card)
                stats: {
                    ActivePL: formatValue(aggregatedData.activePl.admin),
                    BookedPL: formatValue(aggregatedData.profitLoss.admin),
                    Brokerage: formatValue(aggregatedData.brokerage.admin),
                    GrossPL: formatValue(aggregatedData.grossProfitLoss.admin)
                }
            }
            // Only two cards for SuperAdmin view
        ];
        cardGridCols = 4; // 2 cards with full width (12/2 = 6 each)
    } else {
        cards = [
            {
                title: 'Admin',
                stats: {
                    ActivePL: formatValue(aggregatedData.activePl.your),
                    BookedPL: formatValue(aggregatedData.profitLoss.your),
                    Brokerage: formatValue(aggregatedData.brokerage.your),
                    GrossPL: formatValue(aggregatedData.grossProfitLoss.your)
                }
            },
            {
                title: 'Clients',
                stats: {
                    ActivePL: formatValue(aggregatedData.activePl.total),
                    BookedPL: formatValue(aggregatedData.profitLoss.total),
                    Brokerage: formatValue(aggregatedData.brokerage.total),
                    GrossPL: formatValue(aggregatedData.grossProfitLoss.total)
                }
            },
            {
                title: 'SuperAdmin',
                stats: {
                    ActivePL: formatValue(aggregatedData.activePl.admin),
                    BookedPL: formatValue(aggregatedData.profitLoss.admin),
                    Brokerage: formatValue(aggregatedData.brokerage.admin),
                    GrossPL: formatValue(aggregatedData.grossProfitLoss.admin)
                }
            }
        ];
        cardGridCols = 4; // 3 cards
    }

    return (
        <HorizontalCardsPanel>
            {/* System Overview Section */}
            <SystemOverviewSection>
                {/* <Typography className="section-title">System Overview</Typography> */}

                {/* Summary Stats Card - Active Users Info */}
                <CompactCard>
                    <Box sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        height: '100%',
                        flexDirection: 'row', // Always keep in row layout
                        gap: { xs: 0.5, sm: 0 } // Reduced gap on mobile
                    }}>
                        {/* Active Brokers */}
                        {user.role.rolename !== 'broker' && isBrokerView !== true && (<Box sx={{
                            flex: 1,
                            p: { xs: 0.25, sm: 1 }, // Reduced padding on mobile
                            textAlign: 'center',
                            borderRight: '1px solid rgba(255, 255, 255, 0.2)', // Always show border right
                            width: 'auto' // Remove width constraints
                        }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: { xs: 0.25, sm: 0.5 }, mb: { xs: 0.25, sm: 0.5 } }}>
                                <PersonIcon sx={{ fontSize: { xs: '0.875rem', sm: '1rem' }, color: 'rgba(255, 255, 255, 0.8)' }} />
                                <Typography variant="body2" sx={{
                                    fontSize: { xs: '0.65rem', sm: '0.75rem' }, // Smaller on mobile
                                    fontWeight: 600,
                                    color: 'rgba(255, 255, 255, 0.9)',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.02em',
                                    display: { xs: 'none', sm: 'block' } // Hide text on very small screens
                                }}>
                                    Active Brokers
                                </Typography>
                                <Typography variant="body2" sx={{
                                    fontSize: '0.55rem', // Very small text for mobile
                                    fontWeight: 600,
                                    color: 'rgba(255, 255, 255, 0.9)',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.01em',
                                    display: { xs: 'block', sm: 'none' } // Show only on mobile
                                }}>
                                    Brokers
                                </Typography>
                            </Box>
                            <Box sx={{
                                display: 'flex',
                                alignItems: 'baseline',
                                justifyContent: 'center',
                                gap: { xs: 0.25, sm: 0.5 }
                            }}>
                                <Typography sx={{
                                    fontSize: { xs: '1rem', sm: '1.5rem' }, // Smaller on mobile
                                    lineHeight: 1,
                                    fontWeight: 700,
                                    color: '#FFFFFF',
                                }}>
                                    {userCounts?.totalActiveBrokers}
                                </Typography>
                                <Typography sx={{
                                    fontSize: { xs: '0.7rem', sm: '0.9rem' }, // Smaller on mobile
                                    color: 'rgba(255, 255, 255, 0.7)',
                                    fontWeight: 500
                                }}>
                                    / {userCounts?.totalBrokers}
                                </Typography>
                            </Box>
                        </Box>)}
                        {/* Active Clients */}
                        <Box sx={{
                            flex: 1,
                            p: { xs: 0.25, sm: 1 }, // Reduced padding on mobile
                            textAlign: 'center',
                            borderRight: '1px solid rgba(255, 255, 255, 0.2)', // Always show border right
                            width: 'auto' // Remove width constraints
                        }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: { xs: 0.25, sm: 0.5 }, mb: { xs: 0.25, sm: 0.5 } }}>
                                <PeopleAltIcon sx={{ fontSize: { xs: '0.875rem', sm: '1rem' }, color: 'rgba(255, 255, 255, 0.8)' }} />
                                <Typography variant="body2" sx={{
                                    fontSize: { xs: '0.65rem', sm: '0.75rem' }, // Smaller on mobile
                                    fontWeight: 600,
                                    color: 'rgba(255, 255, 255, 0.9)',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.02em',
                                    display: { xs: 'none', sm: 'block' } // Hide text on very small screens
                                }}>
                                    Active Clients
                                </Typography>
                                <Typography variant="body2" sx={{
                                    fontSize: '0.55rem', // Very small text for mobile
                                    fontWeight: 600,
                                    color: 'rgba(255, 255, 255, 0.9)',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.01em',
                                    display: { xs: 'block', sm: 'none' } // Show only on mobile
                                }}>
                                    Clients
                                </Typography>
                            </Box>
                            <Box sx={{
                                display: 'flex',
                                alignItems: 'baseline',
                                justifyContent: 'center',
                                gap: { xs: 0.25, sm: 0.5 }
                            }}>
                                <Typography sx={{
                                    fontSize: { xs: '1.1rem', sm: '1.75rem' }, // Smaller on mobile
                                    lineHeight: 1,
                                    fontWeight: 700,
                                    color: '#FFFFFF',
                                }}>
                                    {userCounts?.totalActiveClients}
                                </Typography>
                                <Typography sx={{
                                    fontSize: { xs: '0.75rem', sm: '1rem' }, // Smaller on mobile
                                    color: 'rgba(255, 255, 255, 0.7)',
                                    fontWeight: 500
                                }}>
                                    / {userCounts?.totalClients}
                                </Typography>
                            </Box>
                        </Box>
                        {/* Trading Clients */}
                        <Box sx={{
                            flex: 1,
                            p: { xs: 0.25, sm: 1 }, // Reduced padding on mobile
                            textAlign: 'center',
                            width: 'auto' // Remove width constraints
                        }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: { xs: 0.25, sm: 0.5 }, mb: { xs: 0.25, sm: 0.5 } }}>
                                <ShowChartIcon sx={{ fontSize: { xs: '0.875rem', sm: '1rem' }, color: 'rgba(255, 255, 255, 0.8)' }} />
                                <Typography variant="body2" sx={{
                                    fontSize: { xs: '0.65rem', sm: '0.75rem' }, // Smaller on mobile
                                    fontWeight: 600,
                                    color: 'rgba(255, 255, 255, 0.9)',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.02em',
                                    display: { xs: 'none', sm: 'block' } // Hide text on very small screens
                                }}>
                                    Trading Clients
                                </Typography>
                                <Typography variant="body2" sx={{
                                    fontSize: '0.55rem', // Very small text for mobile
                                    fontWeight: 600,
                                    color: 'rgba(255, 255, 255, 0.9)',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.01em',
                                    display: { xs: 'block', sm: 'none' } // Show only on mobile
                                }}>
                                    Trading
                                </Typography>
                            </Box>
                            <Typography sx={{
                                fontSize: { xs: '1.2rem', sm: '2rem' }, // Smaller on mobile
                                lineHeight: 1,
                                fontWeight: 700,
                                color: '#FFFFFF'
                            }}>
                                {userCounts?.totalLiveClients}
                            </Typography>
                        </Box>
                    </Box>
                </CompactCard>
            </SystemOverviewSection>

            {/* Admin, Broker and SuperAdmin Stats Cards - Horizontal Layout */}
            <CardsGrid container spacing={0.5}>
                {cards.map((card, index) => (
                    <Grid item xs={12} sm={6} md={cardGridCols} key={card.title}>
                        <StatsCard colorIndex={index}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
                                {card.title === 'Admin' && <SupervisorAccountIcon sx={{ fontSize: '1.5rem', color: cardColors[index]?.iconColor || '#FFE0B2' }} />}
                                {card.title === 'Broker' && <PersonIcon sx={{ fontSize: '1.5rem', color: cardColors[index]?.iconColor || '#FFE0B2' }} />}
                                {card.title === 'Clients' && <PeopleAltIcon sx={{ fontSize: '1.5rem', color: cardColors[index]?.iconColor || '#BBDEFB' }} />}
                                {card.title === 'SuperAdmin' && <AccountBalanceIcon sx={{ fontSize: '1.5rem', color: cardColors[index]?.iconColor || '#E1BEE7' }} />}
                                <Typography
                                    variant="h6"
                                    sx={{
                                        fontSize: '1rem',
                                        fontWeight: 700,
                                        color: '#FFFFFF',
                                        letterSpacing: '0.02em',
                                        textTransform: 'uppercase'
                                    }}>
                                    {card.title}
                                </Typography>
                            </Box>
                            {Object.entries(card.stats).map(([label, value]) => (
                                <StatItem key={label} isGrossPL={label === 'GrossPL'}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        {label === 'ActivePL' && <TrendingUpIcon sx={{ fontSize: '0.875rem', color: 'rgba(255, 255, 255, 0.7)' }} />}
                                        {label === 'BookedPL' && <AccountBalanceWalletIcon sx={{ fontSize: '0.875rem', color: 'rgba(255, 255, 255, 0.7)' }} />}
                                        {label === 'Brokerage' && <CurrencyExchangeIcon sx={{ fontSize: '0.875rem', color: 'rgba(255, 255, 255, 0.7)' }} />}
                                        {label === 'GrossPL' && <MonetizationOnIcon sx={{ fontSize: '0.875rem', color: 'rgba(255, 255, 255, 0.9)' }} />}
                                        <Typography variant="body2">{label}</Typography>
                                    </Box>
                                    <Typography variant="subtitle1" style={{
                                        color: label === 'Brokerage' ? "#FFFFFF" : "#FFFFFF",
                                        fontWeight: label === 'GrossPL' ? 800 : 700
                                    }}>
                                        {value}
                                    </Typography>
                                </StatItem>
                            ))}
                        </StatsCard>
                    </Grid>
                ))}
            </CardsGrid>
            {/* COMEX, WINDEX, and CRYPTO Active Positions Cards */}
            <CardsGrid container spacing={0.5}>
                <Grid item xs={12} lg={4}>
                    <WhiteStatsCard>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <CandlestickChartIcon sx={{ fontSize: '1.3rem', color: '#1976D2' }} />
                                <Typography
                                    variant="h10"
                                    sx={{
                                        fontSize: '0.9rem',
                                        fontWeight: 700,
                                        color: '#1976D2',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.05em'
                                    }}
                                >
                                    COMEX ACTIVE POSITIONS
                                </Typography>
                            </Box>
                            <Button
                                size="small"
                                variant="outlined"
                                onClick={() => handleOpenActiveTradesModal('COMEX')}
                                startIcon={<VisibilityIcon sx={{ fontSize: 14 }} />}
                                sx={{
                                    color: '#1976D2',
                                    borderColor: '#1976D2',
                                    fontSize: '0.65rem',
                                    fontWeight: 700,
                                    padding: '3px 10px',
                                    minWidth: 'auto',
                                    textTransform: 'none',
                                    borderWidth: '1.5px',
                                    '&:hover': {
                                        backgroundColor: 'rgba(25, 118, 210, 0.08)',
                                        borderColor: '#1565C0',
                                        borderWidth: '1.5px'
                                    },
                                    '& .MuiButton-startIcon': {
                                        marginRight: '4px',
                                        marginLeft: 0
                                    }
                                }}
                                title="View Active Trades Details"
                            >
                                Show All
                            </Button>
                        </Box>
                        <WhiteTableHeader>
                            <Typography className="header-cell">Symbol</Typography>
                            <Typography className="header-cell">Buy</Typography>
                            <Typography className="header-cell">Sell</Typography>
                            <Typography className="header-cell">net</Typography>
                        </WhiteTableHeader>
                        {marketData.comex.map((item) => (
                            <WhiteTableRow key={item.name}>
                                <Typography className="symbol">{item.name}</Typography>
                                <Typography className="value positive">+{item.buy.toLocaleString('en-IN')}</Typography>
                                <Typography className="value negative">-{item.sell.toLocaleString('en-IN')}</Typography>
                                <Typography className="value total" style={{ color: getValueColor(item.total) }}>
                                    {item.total.toLocaleString('en-IN')}
                                </Typography>
                            </WhiteTableRow>
                        ))}
                    </WhiteStatsCard>
                </Grid>

                <Grid item xs={12} lg={4}>
                    <WhiteStatsCard>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <DiamondIcon sx={{ fontSize: '1.3rem', color: '#FF9800' }} />
                                <Typography
                                    variant="h10"
                                    sx={{
                                        fontSize: '0.9rem',
                                        fontWeight: 700,
                                        color: '#FF9800',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.05em'
                                    }}
                                >
                                    WINDEX ACTIVE POSITIONS
                                </Typography>
                            </Box>
                            <Button
                                size="small"
                                variant="outlined"
                                onClick={() => handleOpenActiveTradesModal('WINDEX')}
                                startIcon={<VisibilityIcon sx={{ fontSize: 14 }} />}
                                sx={{
                                    color: '#FF9800',
                                    borderColor: '#FF9800',
                                    fontSize: '0.65rem',
                                    fontWeight: 700,
                                    padding: '3px 10px',
                                    minWidth: 'auto',
                                    textTransform: 'none',
                                    borderWidth: '1.5px',
                                    '&:hover': {
                                        backgroundColor: 'rgba(255, 152, 0, 0.08)',
                                        borderColor: '#F57C00',
                                        borderWidth: '1.5px'
                                    },
                                    '& .MuiButton-startIcon': {
                                        marginRight: '4px',
                                        marginLeft: 0
                                    }
                                }}
                                title="View Active Trades Details"
                            >
                                Show All
                            </Button>
                        </Box>
                        <WhiteTableHeader>
                            <Typography className="header-cell">Symbol</Typography>
                            <Typography className="header-cell">Buy</Typography>
                            <Typography className="header-cell">Sell</Typography>
                            <Typography className="header-cell">net</Typography>
                        </WhiteTableHeader>
                        {marketData.windex.map((item) => (
                            <WhiteTableRow key={item.name}>
                                <Typography className="symbol">{item.name}</Typography>
                                <Typography className="value positive">+{item.buy.toLocaleString('en-IN')}</Typography>
                                <Typography className="value negative">-{item.sell.toLocaleString('en-IN')}</Typography>
                                <Typography className="value total" style={{ color: getValueColor(item.total) }}>
                                    {item.total.toLocaleString('en-IN')}
                                </Typography>
                            </WhiteTableRow>
                        ))}
                    </WhiteStatsCard>
                </Grid>

                <Grid item xs={12} lg={4}>
                    <WhiteStatsCard>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <CurrencyExchangeIcon sx={{ fontSize: '1.3rem', color: '#9C27B0' }} />
                                <Typography
                                    variant="h10"
                                    sx={{
                                        fontSize: '0.9rem',
                                        fontWeight: 700,
                                        color: '#9C27B0',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.05em'
                                    }}
                                >
                                    CRYPTO ACTIVE POSITIONS
                                </Typography>
                            </Box>
                            <Button
                                size="small"
                                variant="outlined"
                                onClick={() => handleOpenActiveTradesModal('CRYPTO')}
                                startIcon={<VisibilityIcon sx={{ fontSize: 14 }} />}
                                sx={{
                                    color: '#9C27B0',
                                    borderColor: '#9C27B0',
                                    fontSize: '0.65rem',
                                    fontWeight: 700,
                                    padding: '3px 10px',
                                    minWidth: 'auto',
                                    textTransform: 'none',
                                    borderWidth: '1.5px',
                                    '&:hover': {
                                        backgroundColor: 'rgba(156, 39, 176, 0.08)',
                                        borderColor: '#7B1FA2',
                                        borderWidth: '1.5px'
                                    },
                                    '& .MuiButton-startIcon': {
                                        marginRight: '4px',
                                        marginLeft: 0
                                    }
                                }}
                                title="View Active Trades Details"
                            >
                                Show All
                            </Button>
                        </Box>
                        <WhiteTableHeader>
                            <Typography className="header-cell">Symbol</Typography>
                            <Typography className="header-cell">Buy</Typography>
                            <Typography className="header-cell">Sell</Typography>
                            <Typography className="header-cell">net</Typography>
                        </WhiteTableHeader>
                        {marketData.crypto.map((item) => (
                            <WhiteTableRow key={item.name}>
                                <Typography className="symbol">{item.name}</Typography>
                                <Typography className="value positive">+{item.buy.toLocaleString('en-IN')}</Typography>
                                <Typography className="value negative">-{item.sell.toLocaleString('en-IN')}</Typography>
                                <Typography className="value total" style={{ color: getValueColor(item.total) }}>
                                    {item.total.toLocaleString('en-IN')}
                                </Typography>
                            </WhiteTableRow>
                        ))}
                    </WhiteStatsCard>
                </Grid>
            </CardsGrid>            {/* Gross PL Overview Card - COMEX, WINDEX, CRYPTO */}
            <CardsGrid container spacing={0.5}>
                <Grid item xs={12}>
                    <PLOverviewCard isPositive={
                        (+balanceDetail?.COMEX?.grossProfitLoss?.total +
                            +balanceDetail?.WINDEX?.grossProfitLoss?.total +
                            +balanceDetail?.CRYPTO?.grossProfitLoss?.total) >= 0
                    }>
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 1,
                            mb: 1.5,
                            pb: 0.75,
                            borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
                            flexDirection: { xs: 'column', sm: 'row' },
                            textAlign: { xs: 'center', sm: 'left' }
                        }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <ShowChartIcon sx={{ fontSize: '1.5rem', color: +balanceDetail?.COMEX?.grossProfitLoss?.total + +balanceDetail?.WINDEX?.grossProfitLoss?.total + +balanceDetail?.CRYPTO?.grossProfitLoss?.total >= 0 ? '#2E7D32' : '#C62828' }} />
                                <Typography
                                    sx={{
                                        fontSize: '1rem',
                                        fontWeight: 700,
                                        color: +balanceDetail?.COMEX?.grossProfitLoss?.total + +balanceDetail?.WINDEX?.grossProfitLoss?.total + +balanceDetail?.CRYPTO?.grossProfitLoss?.total >= 0 ? '#2E7D32' : '#C62828',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.03em'
                                    }}>
                                    Gross Profit/Loss Overview
                                </Typography>
                            </Box>
                            <Typography sx={{
                                fontSize: { xs: '1.5rem', sm: '2rem' },
                                fontWeight: 800,
                                color: +balanceDetail?.COMEX?.grossProfitLoss?.total + +balanceDetail?.WINDEX?.grossProfitLoss?.total + +balanceDetail?.CRYPTO?.grossProfitLoss?.total >= 0 ? '#1B5E20' : '#B71C1C',
                                letterSpacing: '0.02em',
                            }}>
                                {formatValue(+balanceDetail?.COMEX?.grossProfitLoss?.total + +balanceDetail?.WINDEX?.grossProfitLoss?.total + +balanceDetail?.CRYPTO?.grossProfitLoss?.total)}
                            </Typography>
                        </Box>

                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 2 }}>
                            {/* COMEX */}
                            <Box sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 0.5,
                                p: 1.5,
                                borderRadius: 1,
                                backgroundColor: 'rgba(255, 255, 255, 0.5)',
                                border: '1px solid rgba(0, 0, 0, 0.08)'
                            }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                                    <CandlestickChartIcon sx={{ fontSize: '1rem', color: '#1976D2' }} />
                                    <Typography sx={{
                                        fontSize: '0.85rem',
                                        fontWeight: 700,
                                        color: '#1976D2',
                                        textTransform: 'uppercase'
                                    }}>
                                        COMEX
                                    </Typography>
                                </Box>
                                <Typography sx={{
                                    fontSize: '1.25rem',
                                    fontWeight: 800,
                                    color: +balanceDetail?.COMEX?.grossProfitLoss?.total >= 0 ? '#1B5E20' : '#B71C1C',
                                }}>
                                    {formatValue(+balanceDetail?.COMEX?.grossProfitLoss?.total)}
                                </Typography>
                            </Box>

                            {/* WINDEX */}
                            <Box sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 0.5,
                                p: 1.5,
                                borderRadius: 1,
                                backgroundColor: 'rgba(255, 255, 255, 0.5)',
                                border: '1px solid rgba(0, 0, 0, 0.08)'
                            }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                                    <DiamondIcon sx={{ fontSize: '1rem', color: '#FF9800' }} />
                                    <Typography sx={{
                                        fontSize: '0.85rem',
                                        fontWeight: 700,
                                        color: '#FF9800',
                                        textTransform: 'uppercase'
                                    }}>
                                        WINDEX
                                    </Typography>
                                </Box>
                                <Typography sx={{
                                    fontSize: '1.25rem',
                                    fontWeight: 800,
                                    color: +balanceDetail?.WINDEX?.grossProfitLoss?.total >= 0 ? '#1B5E20' : '#B71C1C',
                                }}>
                                    {formatValue(+balanceDetail?.WINDEX?.grossProfitLoss?.total)}
                                </Typography>
                            </Box>

                            {/* CRYPTO */}
                            <Box sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 0.5,
                                p: 1.5,
                                borderRadius: 1,
                                backgroundColor: 'rgba(255, 255, 255, 0.5)',
                                border: '1px solid rgba(0, 0, 0, 0.08)'
                            }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                                    <CurrencyExchangeIcon sx={{ fontSize: '1rem', color: '#9C27B0' }} />
                                    <Typography sx={{
                                        fontSize: '0.85rem',
                                        fontWeight: 700,
                                        color: '#9C27B0',
                                        textTransform: 'uppercase'
                                    }}>
                                        CRYPTO
                                    </Typography>
                                </Box>
                                <Typography sx={{
                                    fontSize: '1.25rem',
                                    fontWeight: 800,
                                    color: +balanceDetail?.CRYPTO?.grossProfitLoss?.total >= 0 ? '#1B5E20' : '#B71C1C',
                                }}>
                                    {formatValue(+balanceDetail?.CRYPTO?.grossProfitLoss?.total)}
                                </Typography>
                            </Box>
                        </Box>
                    </PLOverviewCard>
                </Grid>
            </CardsGrid>

            {/* Top Gainers/Losers Modal */}
            <TopGainersLosersModal
                open={modalOpen}
                onClose={handleCloseModal}
                userId={currentUserId}
                segment={modalSegment}
            />

            {/* Active Trades Details Modal */}
            <ActiveTradesDetailsModal
                open={activeTradesModalOpen}
                onClose={handleCloseActiveTradesModal}
                userId={currentUserId}
                segment={activeTradesSegment}
            />
        </HorizontalCardsPanel>
    );
};

export default DashboardCardsPanel;
